/**
 * PersonalDashboard.jsx – Zentrales Personal-Verwaltungs-Center
 * 5 Tabs: Mitarbeiter | Onboarding | Verträge | Vertragsvorlagen | Betriebsvereinbarungen | Firmenprofil
 * URL: /admin/personal?tab=mitarbeiter|onboarding|vertraege|vorlagen|betriebsvereinbarungen
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { PersonalboqenWizard } from '../../components/PersonalboqenWizard';
import { PersonalboqenEditModal } from '../../components/PersonalboqenEditModal';
import { ContractCreateModal } from '../../components/ContractCreateModal';
import { BetriebsvereinbarungenPage } from './BetriebsvereinbarungenPage';
import { DokumentOrdnerVerwaltung } from '../../components/DokumentOrdnerVerwaltung';
import axios from 'axios';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import SignaturePad from '../../components/ui/SignaturePad';
import { RvNachholenDialog } from '../../components/RvNachholenDialog';
import {
  Users, ClipboardList, FileText, Shield, Building2,
  Plus, Edit2, Trash2, FileDown, Link as LinkIcon, Mail, Copy, Archive,
  ChevronDown, CheckCircle2, Clock, Loader2,
  AlertCircle, Eye, EyeOff, RefreshCw, Package, Save, BookOpen, Folder,
} from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'mitarbeiter',           label: 'Mitarbeiter',           icon: Users },
  { id: 'onboarding',           label: 'Personalbögen',         icon: ClipboardList },
  { id: 'vertraege',            label: 'Verträge',              icon: FileText },
  { id: 'vorlagen',             label: 'Vertragsvorlagen',      icon: Shield },
  { id: 'betriebsvereinbarungen', label: 'Betriebsvereinbarungen', icon: BookOpen },
  { id: 'dok-ordner',           label: 'Dok.-Ordner',           icon: Folder },
  { id: 'firmenprofil',         label: 'Firmenprofil',          icon: Building2 },
];

const SIGNING_STATUS = {
  draft:     { label: 'Entwurf',              cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  signed_an: { label: 'Warte auf Admin',      cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  signed_ag: { label: 'Admin unterschrieben', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  complete:  { label: 'Vollständig',          cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
};

const BOGEN_STATUS = {
  draft:        { label: 'Link ausstehend',         cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  admin_filled: { label: 'Wartet auf Mitarbeiter',  cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  link_sent:    { label: 'Wartet auf Mitarbeiter',  cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  submitted:    { label: 'Zur Prüfung',             cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  approved:     { label: 'Abgeschlossen',           cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
};

const CONTRACT_TYPES = { vz: 'Vollzeit', tz: 'Teilzeit', minijob: 'Minijob', custom: 'Sonstiges' };

export default function PersonalDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const activeTab = searchParams.get('tab') || 'mitarbeiter';

  useEffect(() => {
    if (activeTab === 'mitarbeiter') fetchArchivedEmployees();
  }, [activeTab]); // eslint-disable-line
  const setTab = (t) => setSearchParams({ tab: t });

  // Vorauswahl: ?employee=ID — wird NACH employees-useState platziert (Temporal Dead Zone fix)
  const preSelectEmployeeId = searchParams.get('employee') || '';

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // ── State ────────────────────────────────────────────────────────────────
  const [employees, setEmployees] = useState([]);
  const [personalboqens, setPersonalboqens] = useState([]);
  const [signings, setSignings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState({ employees: false, boqens: false, signings: false, templates: false });

  const [showWizard, setShowWizard] = useState(false);
  const [editingBogen, setEditingBogen] = useState(null);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [expandedSigning, setExpandedSigning] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editTemplate, setEditTemplate] = useState(null);
  // Hardware-Export
  const [showHWDialog, setShowHWDialog] = useState(false);
  const [rvNachholenBogen, setRvNachholenBogen] = useState(null);
  const [hwFilter, setHwFilter] = useState('all');
  const [hwSelection, setHwSelection] = useState([]);
  const [exportingPdf, setExportingPdf] = useState(false);
  // Direktvertrag
  const [showDirectContract, setShowDirectContract] = useState(false);
  const [dcStep, setDcStep] = useState(1);
  const [dcEmployeeId, setDcEmployeeId] = useState('');
  const [dcTemplateId, setDcTemplateId] = useState('');
  const [dcLocationId, setDcLocationId] = useState('');
  const [dcVars, setDcVars] = useState({ vorname:'', nachname:'', eintrittsdatum:'', job_bezeichnung:'', wochenstunden:'', monatsstunden:'', stundenlohn:'', monatslohn:'', urlaubstage:'20', an_strasse:'', an_plz_ort:'', an_geburtsdatum:'', arbeitstage_woche:'', _stundenart:'wochenstunden', probezeit_dauer:'', befristung_bis:'', hochschule:'', studiengang:'', matrikelnummer:'', max_stunden_semester:'', max_stunden_semesterferien:'', taetigkeit:'', lohnerhoehung_datum:'', neuer_stundenlohn:'', neuer_monatslohn:'', zusatz_info:'' });
  const [dcAlreadySigned, setDcAlreadySigned] = useState(false);
  const [dcSignedDate, setDcSignedDate] = useState('');
  const [dcSaving, setDcSaving] = useState(false);
  const [showDcPreview, setShowDcPreview] = useState(false);
  const [dcPreviewMode, setDcPreviewMode] = useState('rendered'); // 'rendered' | 'raw'
  const [dcEditableText, setDcEditableText] = useState('');       // Editierbarer Vertragstext

  // Firmenprofil
  const [locations, setLocations] = useState([]);
  const [empLocationFilter, setEmpLocationFilter] = useState('all');
  const [firmForms, setFirmForms] = useState({});
  const [firmSaving, setFirmSaving] = useState(null);

  // AG-Signatur Dialog
  const [showSignAG, setShowSignAG] = useState(null); // signing object
  const [agSigRef, setAgSigRef] = useState(null);
  const [agDatum, setAgDatum] = useState('');
  const [agOrt, setAgOrt] = useState('');

  // 1-Klick Vertragsgenerierung aus Personalbogen
  const [contractCreateBogen, setContractCreateBogen] = useState(null);
  const [agSigning, setAgSigning] = useState(false);
  // RV-Befreiung: Arbeitgeber-Gegenzeichnung (Personalbogen-RV, NICHT Contract-RV)
  const [showSignRvAG, setShowSignRvAG] = useState(null); // bogen object
  const [rvSigRef, setRvSigRef] = useState(null);
  const [rvDatum, setRvDatum] = useState('');
  const [rvOrt, setRvOrt] = useState('');
  const [rvEingang, setRvEingang] = useState('');
  const [rvSigning, setRvSigning] = useState(false);
  // Signierlink-Dialog: zeigt Link + PIN nach Generierung
  const [signingLinkInfo, setSigningLinkInfo] = useState(null); // { url, pin_code, name }
  // Account-Erstellt-Dialog: zeigt 4-stelligen PIN prominent an
  const [newAccountInfo, setNewAccountInfo] = useState(null); // { pin, email, name }

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(l => ({ ...l, employees: true, boqens: true, signings: true, templates: true }));
    try {
      const [empR, boqR, sigR, tplR, locR] = await Promise.allSettled([
        axios.get(`${API}/admin/employees`, { headers }),
        axios.get(`${API}/personalbogen`, { headers }),
        axios.get(`${API}/contracts/signing`, { headers }),
        axios.get(`${API}/contracts/templates`, { headers }),
        axios.get(`${API}/admin/locations`, { headers }),
      ]);
      if (empR.status === 'fulfilled') setEmployees(empR.value.data || []);
      if (boqR.status === 'fulfilled') setPersonalboqens(boqR.value.data || []);
      if (sigR.status === 'fulfilled') setSignings(sigR.value.data || []);
      if (tplR.status === 'fulfilled') setTemplates(tplR.value.data || []);
      if (locR.status === 'fulfilled') {
        const locs = locR.value.data || [];
        setLocations(locs);
        // Initialize firmForms from existing location data
        const forms = {};
        locs.forEach(loc => {
          forms[loc.id] = {
            legal_name:            loc.legal_name || '',
            legal_owner:           loc.legal_owner || '',
            legal_address:         loc.legal_address || '',
            legal_plz_ort:         loc.legal_plz_ort || '',
            legal_unterzeichner:   loc.legal_unterzeichner || '',
            legal_betriebsnummer:  loc.legal_betriebsnummer || '',
            legal_steuernummer:    loc.legal_steuernummer || '',
            legal_ust_id:          loc.legal_ust_id || '',
            legal_registergericht: loc.legal_registergericht || '',
            legal_hrb:             loc.legal_hrb || '',
          };
        });
        setFirmForms(forms);
      }
    } finally {
      setLoading({ employees: false, boqens: false, signings: false, templates: false });
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Vorauswahl: ?employee=ID → Direktvertrag-Wizard öffnen + Mitarbeiter vorauswählen
  const preSelectDone = React.useRef(false);
  useEffect(() => {
    if (preSelectDone.current || !preSelectEmployeeId || !employees.length) return;
    preSelectDone.current = true;
    // Wizard-State sauber initialisieren (wie openDirectContract, aber OHNE dcEmployeeId zu löschen)
    setDcStep(1);
    setDcTemplateId(''); setDcLocationId('');
    setDcVars({ vorname:'', nachname:'', eintrittsdatum:'', job_bezeichnung:'', wochenstunden:'', monatsstunden:'', stundenlohn:'', monatslohn:'', urlaubstage:'20', an_strasse:'', an_plz_ort:'', an_geburtsdatum:'', arbeitstage_woche:'', _stundenart:'wochenstunden', probezeit_dauer:'', befristung_bis:'', hochschule:'', studiengang:'', matrikelnummer:'', max_stunden_semester:'', max_stunden_semesterferien:'', taetigkeit:'', lohnerhoehung_datum:'', neuer_stundenlohn:'', neuer_monatslohn:'', zusatz_info:'' });
    setDcAlreadySigned(false); setDcSignedDate('');
    setShowDcPreview(false); setDcPreviewMode('rendered'); setDcEditableText('');
    // Jetzt Mitarbeiter vorauswählen + Wizard öffnen
    if (activeTab !== 'vertraege') setTab('vertraege');
    dcSelectEmployee(preSelectEmployeeId);
    setShowDirectContract(true);
  }, [employees]); // eslint-disable-line

  // Editierbaren Text initialisieren wenn Vorschau geöffnet wird
  useEffect(() => {
    if (showDcPreview && dcPreviewMode === 'rendered' && !dcEditableText) {
      setDcEditableText(renderDcPreview());
    }
  }, [showDcPreview]); // eslint-disable-line



  // ── Helpers ───────────────────────────────────────────────────────────────
  const appBase = window.location.origin;
  const pendingBoqens  = personalboqens.filter(b => b.status !== 'approved');
  const archivedBoqens = personalboqens.filter(b => b.status === 'approved');
  const pendingSignatures = signings.filter(s => s.status === 'signed_an').length;

  // Archivieren / Löschen Dialog
  const [archiveDialog, setArchiveDialog] = useState(null); // { emp, step: 'choose'|'archiving'|'deleting'|'zeugnis' }
  const [archivedEmployees, setArchivedEmployees] = useState([]);
  const [showArchive, setShowArchive] = useState(false);
  const [showZeugnisForArchive, setShowZeugnisForArchive] = useState(null); // emp for zeugnis

  // Archivierte Mitarbeiter laden
  const fetchArchivedEmployees = async () => {
    try {
      const res = await axios.get(`${API}/admin/employees?include_archived=true`, { headers });
      setArchivedEmployees((res.data || []).filter(e => e.is_archived));
    } catch { setArchivedEmployees([]); }
  };

  const handleArchiveEmployee = async (emp) => {
    try {
      await axios.put(`${API}/admin/employees/${emp.id}/archive`, {}, { headers });
      toast.success(`"${emp.name || emp.email}" wurde archiviert`);
      setArchiveDialog(null);
      fetchAll();
      fetchArchivedEmployees();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Archivieren'); }
  };

  const handleRestoreEmployee = async (emp) => {
    try {
      await axios.put(`${API}/admin/employees/${emp.id}/restore`, {}, { headers });
      toast.success(`"${emp.name || emp.email}" wurde wiederhergestellt`);
      fetchAll();
      fetchArchivedEmployees();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handlePermanentDelete = async (emp) => {
    try {
      await axios.delete(`${API}/admin/employees/${emp.id}`, { headers });
      toast.success('Mitarbeiter endgültig gelöscht');
      setArchiveDialog(null);
      fetchAll();
      fetchArchivedEmployees();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleDeleteEmployee = async (emp) => {
    // Prüfe ob bereits ein Zeugnis existiert (für spätere Entscheidung)
    let hasZeugnis = false;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/api/arbeitszeugnis/by-employee/${emp.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      hasZeugnis = Array.isArray(res.data) && res.data.length > 0;
    } catch { /* silent */ }
    setArchiveDialog({ emp, step: 'choose', hasZeugnis });
  };

  const handleDeleteBogen = async (id) => {
    if (!window.confirm('Bogen wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/personalbogen/${id}`, { headers });
      toast.success('Bogen gelöscht');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleCreateAccount = async (bogen) => {
    try {
      const res = await axios.post(`${API}/personalbogen/${bogen.id}/create-account`, {}, { headers });
      const name = bogen.mitarbeiter_name ||
        `${(bogen.personal?.vorname || '')} ${(bogen.personal?.familienname || '')}`.trim() ||
        'Mitarbeiter';
      setNewAccountInfo({ pin: res.data.temp_password, email: res.data.email, name });
      fetchAll();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Fehler'));
    }
  };

  const handleSendBogenEmail = async (bogen) => {
    if (!bogen.mitarbeiter_email) { toast.error('Keine E-Mail-Adresse im Personalbogen'); return; }
    try {
      const res = await axios.post(
        `${API}/personalbogen/public/${bogen.token}/send-pin-email`,
        { email: bogen.mitarbeiter_email },
        { headers }
      );
      if (res?.data?.ok) {
        toast.success(`PIN-Link an ${bogen.mitarbeiter_email} gesendet!`);
      } else {
        toast.error('E-Mail konnte nicht gesendet werden');
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'E-Mail konnte nicht gesendet werden'), { duration: 8000 });
    }
  };

  const downloadPDF = async (url, filename) => {
    try {
      const res = await axios.get(url, { headers, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      a.download = filename; a.click();
    } catch { toast.error('PDF-Fehler'); }
  };

  const handleDeleteTemplate = async (t) => {
    if (!window.confirm(`"${t.name}" löschen?`)) return;
    try {
      await axios.delete(`${API}/contracts/templates/${t.id}`, { headers });
      toast.success('Vorlage gelöscht');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleSeedTemplates = async () => {
    try {
      await axios.post(`${API}/contracts/templates/seed`, {}, { headers });
      toast.success('Start-Templates eingespielt!');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const openDirectContract = () => {
    setDcStep(1); setDcEmployeeId(''); setDcTemplateId(''); setDcLocationId('');
    setDcVars({ vorname:'', nachname:'', eintrittsdatum:'', job_bezeichnung:'', wochenstunden:'', monatsstunden:'', stundenlohn:'', monatslohn:'', urlaubstage:'20', an_strasse:'', an_plz_ort:'', an_geburtsdatum:'', arbeitstage_woche:'', _stundenart:'wochenstunden', probezeit_dauer:'', befristung_bis:'', hochschule:'', studiengang:'', matrikelnummer:'', max_stunden_semester:'', max_stunden_semesterferien:'', taetigkeit:'', lohnerhoehung_datum:'', neuer_stundenlohn:'', neuer_monatslohn:'', zusatz_info:'' });
    setDcAlreadySigned(false); setDcSignedDate('');
    setShowDcPreview(false);
    setDcPreviewMode('rendered');
    setDcEditableText('');
    setShowDirectContract(true);
  };

  // Lokales Template-Rendering für Vorschau (client-side, ohne Backend)
  const renderDcPreview = () => {
    const tmpl = templates.find(t => t.id === dcTemplateId);
    if (!tmpl) return 'Keine Vorlage ausgewählt.';
    const loc = locations.find(l => l.id === dcLocationId) || locations[0] || {};
    const today = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const fmtDate = (s) => {
      if (!s) return '–';
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [y, m, d] = s.split('-');
        return `${d}.${m}.${y}`;
      }
      return s;
    };
    // Fix D: urlaubstage_berechnet identisch zur Backend-Logik berechnen
    const urlaubstage = parseInt(dcVars.urlaubstage || '20', 10);
    const arbeitstage = parseInt(dcVars.arbeitstage_woche || '5', 10);
    const urlaubBerechnet = arbeitstage < 5 ? Math.round(urlaubstage / 5 * arbeitstage) : urlaubstage;

    // Fix D: Alle Variablen aus dcVars – KEINE hardcodierten Fallbacks mehr die vom Backend abweichen
    const vars = {
      // Mitarbeiter
      vorname:           dcVars.vorname || '–',
      nachname:          dcVars.nachname || '–',
      an_strasse:        dcVars.an_strasse || '–',
      an_plz_ort:        dcVars.an_plz_ort || '–',
      an_geburtsdatum:   fmtDate(dcVars.an_geburtsdatum),
      // Vertrag
      eintrittsdatum:    fmtDate(dcVars.eintrittsdatum),
      job_bezeichnung:   dcVars.job_bezeichnung || '–',
      taetigkeit:        dcVars.taetigkeit || dcVars.job_bezeichnung || '–',
      berufsbezeichnung: dcVars.job_bezeichnung || '–',
      wochenstunden:     dcVars.wochenstunden || dcVars.monatsstunden || '–',
      monatsstunden:     dcVars.monatsstunden || dcVars.wochenstunden || '–',
      stundenlohn:       dcVars.stundenlohn || '–',
      monatslohn:        dcVars.monatslohn || '–',
      vergutung:         dcVars.stundenlohn ? `${dcVars.stundenlohn} €/Std.` : (dcVars.monatslohn ? `${dcVars.monatslohn} €/Monat` : '–'),
      urlaubstage:       String(urlaubstage),
      urlaubstage_jahr:  String(urlaubstage),
      urlaubstage_berechnet: String(urlaubBerechnet),
      arbeitstage_woche: String(arbeitstage),
      heutiges_datum:    today,
      // Firma (aus Standort)
      firma_name:        loc.legal_name || loc.name || '–',
      firma_inhaber:     loc.legal_owner || '–',
      firma_adresse:     loc.legal_address || loc.address || '–',
      firma_plz_ort:     loc.legal_plz_ort || loc.city || '–',
      firma_unterzeichner: loc.legal_unterzeichner || loc.legal_owner || '–',
      standort:          loc.city || loc.name || '–',
      // Fix D: Aus dcVars lesen (jetzt durch Fix B befüllt), Fallback = Backend-Default
      probezeit_dauer:            dcVars.probezeit_dauer    || 'sechs Monate',
      hochschule:                 dcVars.hochschule         || '–',
      studiengang:                dcVars.studiengang        || '–',
      matrikelnummer:             dcVars.matrikelnummer     || '–',
      max_stunden_semester:       dcVars.max_stunden_semester       || '20',
      max_stunden_semesterferien: dcVars.max_stunden_semesterferien || '40',
      befristung_bis:             fmtDate(dcVars.befristung_bis),
      // Zusatzvereinbarung
      lohnerhoehung_datum: dcVars.lohnerhoehung_datum ? fmtDate(dcVars.lohnerhoehung_datum) : today,
      neuer_stundenlohn:   dcVars.neuer_stundenlohn || dcVars.stundenlohn || '–',
      neuer_monatslohn:    dcVars.neuer_monatslohn  || dcVars.monatslohn  || '–',
      zusatz_info:         dcVars.zusatz_info || '',
    };
    // befristung_text: befristeter Satz ODER unbefristet – identisch zur Backend-Logik
    const bef = dcVars.befristung_bis ? fmtDate(dcVars.befristung_bis) : '';
    vars.befristung_text = (bef && bef !== '–')
      ? `Das Arbeitsverhältnis wird auf bestimmte Zeit bis zum ${bef} geschlossen. Nach Ablauf endet das Arbeitsverhältnis automatisch, ohne dass es einer Kündigung bedarf.`
      : 'Das Arbeitsverhältnis wird auf unbestimmte Zeit geschlossen.';

    let text = tmpl.template_text;
    Object.entries(vars).forEach(([k, v]) => {
      text = text.replaceAll(`{{${k}}}`, String(v ?? '–'));
    });
    return text;
  };

  // Helper: DD.MM.YYYY → YYYY-MM-DD (für date-Inputs)
  const toDateInput = (val) => {
    if (!val) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(val)) {
      const [d, m, y] = val.split('.');
      return `${y}-${m}-${d}`;
    }
    return val;
  };

  // Lohnbetrag aus Freitext ("14,00 €/Std." → "14,00") – spiegelt Backend _parse_wage_number
  const parseWage = (raw) => {
    if (!raw) return '';
    const m = String(raw).match(/(\d{1,4}[.,]?\d{0,2})/);
    return m ? m[1].replace('.', ',') : '';
  };

  const dcSelectEmployee = (empId) => {
    setDcEmployeeId(empId);
    const emp = employees.find(e => e.id === empId);
    if (!emp) return;

    const parts = (emp.name || '').split(' ');

    // Passenden Personalbogen suchen
    const bogen = personalboqens.find(b =>
      b.employee_id === empId ||
      b.user_id === empId ||
      (b.personal?.email && emp.email &&
       b.personal.email.toLowerCase() === emp.email.toLowerCase())
    );

    const pe = bogen?.personal || {};
    const ag = bogen?.arbeitgeber || {};
    const be = bogen?.beschaeftigung || {};

    // Adresse aus Personalbogen oder User-Dokument
    const boStrasse = `${pe.strasse || ''} ${pe.hausnummer || ''}`.trim();
    const boPlzOrt  = `${pe.plz || ''} ${pe.ort || ''}`.trim();

    // Stunden-Art bestimmen
    const hasWochen  = !!(be.stunden_pro_woche  || ag.stunden_pro_woche);
    const hasMonat   = !!(be.stunden_pro_monat  || ag.stunden_pro_monat);
    const stundenart = hasWochen ? 'wochenstunden' : hasMonat ? 'monatsstunden' : 'wochenstunden';

    // Arbeitstage/Woche: aus Personalbogen, sonst aus Stunden berechnen
    const arbTage = be.arbeitstage_woche || ag.arbeitstage_woche
      || (be.stunden_pro_woche ? String(Math.round(parseFloat(be.stunden_pro_woche) / 8)) : '')
      || '';

    setDcVars(v => ({
      ...v,
      // Name
      vorname:       pe.vorname        || parts[0] || '',
      nachname:      pe.familienname   || parts.slice(1).join(' ') || '',
      // Adresse
      an_strasse:    boStrasse         || emp.strasse    || '',
      an_plz_ort:    boPlzOrt          || emp.plz_ort    || '',
      an_geburtsdatum: toDateInput(pe.geburtsdatum || emp.geburtsdatum || ''),
      // Vertragsdaten
      eintrittsdatum:  toDateInput(ag.eintrittsdatum || ''),
      // Fix B: ausgeubte_taetigkeit hat Vorrang vor berufsbezeichnung für job_bezeichnung
      job_bezeichnung: ag.ausgeubte_taetigkeit || ag.berufsbezeichnung || v.job_bezeichnung || '',
      // Fix B: taetigkeit separat (Aufgabenbeschreibung, identisch mit job_bezeichnung als Fallback)
      taetigkeit:      ag.ausgeubte_taetigkeit || ag.berufsbezeichnung || '',
      wochenstunden:   be.stunden_pro_woche  || ag.stunden_pro_woche  || '',
      monatsstunden:   be.stunden_pro_monat  || ag.stunden_pro_monat  || '',
      stundenlohn:     ag.stundenlohn   || be.stundenlohn || parseWage(ag.vergutung) || '',
      monatslohn:      ag.monatslohn    || '',
      urlaubstage:     ag.urlaubstage   || v.urlaubstage  || '20',
      arbeitstage_woche: arbTage,
      _stundenart:     stundenart,
      // Fix B: fehlende Felder aus Personalbogen lesen
      probezeit_dauer:            ag.probezeit_dauer || '',
      befristung_bis:             toDateInput(ag.befristung_bis || ''),
      hochschule:                 pe.hochschule      || '',
      studiengang:                pe.studiengang     || '',
      matrikelnummer:             pe.matrikelnummer  || '',
      max_stunden_semester:       ag.max_stunden_semester        ? String(ag.max_stunden_semester)       : '',
      max_stunden_semesterferien: ag.max_stunden_semesterferien  ? String(ag.max_stunden_semesterferien) : '',
      // Zusatz-Felder
      neuer_stundenlohn: ag.stundenlohn || '',
      neuer_monatslohn:  ag.monatslohn  || '',
    }));
  };

  const handleCreateDirectContract = async () => {
    if (!dcEmployeeId || !dcTemplateId) { toast.error('Bitte Mitarbeiter und Vorlage wählen'); return; }
    setDcSaving(true);
    try {
      // Wenn der Admin den Vertragstext in der Vorschau bearbeitet hat → editierten Text verwenden
      const payload = {
        employee_id: dcEmployeeId,
        template_id: dcTemplateId,
        location_id: dcLocationId || undefined,
        variables: dcVars,
        already_signed: dcAlreadySigned,
        signed_date: dcAlreadySigned ? dcSignedDate || null : null,
        ...(dcEditableText.trim() && { override_text: dcEditableText.trim() }),
      };
      await axios.post(`${API}/contracts/signing/create-direct`, payload, { headers });
      toast.success('Vertrag gespeichert');
      setShowDirectContract(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setDcSaving(false); }
  };

  const handleSignAG = async () => {
    if (!agSigRef || agSigRef.isEmpty()) { toast.error('Bitte Unterschrift zeichnen'); return; }
    setAgSigning(true);
    try {
      const sigData = agSigRef.toDataURL('image/png');
      await axios.post(`${API}/contracts/signing/${showSignAG.id}/sign-ag`, {
        signature_ag: sigData,
        datum: agDatum || new Date().toLocaleDateString('de-DE'),
        ort: agOrt || '',
      }, { headers });
      toast.success('Vertrag gegengezeichnet');
      setShowSignAG(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Unterschreiben'); }
    finally { setAgSigning(false); }
  };

  const openRvSign = (bogen) => {
    const today = new Date().toLocaleDateString('de-DE');
    setRvDatum(today);
    setRvEingang(today);
    setRvOrt('');
    setShowSignRvAG(bogen);
  };

  const handleSignRvAG = async () => {
    if (!rvSigRef || rvSigRef.isEmpty()) { toast.error('Bitte Unterschrift zeichnen'); return; }
    setRvSigning(true);
    try {
      const sigData = rvSigRef.toDataURL('image/png');
      // WICHTIG: admin-rv-complete überschreibt NICHT die Mitarbeiter-Unterschrift (rv_antrag_an),
      // solange wir keine an_signature/an_ort mitsenden -> nur der Arbeitgeber-Teil wird ergänzt.
      await axios.put(`${API}/personalbogen/${showSignRvAG.id}/admin-rv-complete`, {
        signature_ag: sigData,
        datum_unterschrift_ag: rvDatum || new Date().toLocaleDateString('de-DE'),
        eingangsdatum: rvEingang || '',
        ort_ag: rvOrt || '',
      }, { headers });
      toast.success('RV-Befreiung als Arbeitgeber bestätigt');
      setShowSignRvAG(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Bestätigen'); }
    finally { setRvSigning(false); }
  };

  const handleFirmSave = async (locationId) => {
    setFirmSaving(locationId);
    try {
      await axios.put(`${API}/admin/locations/${locationId}/legal-profile`, firmForms[locationId] || {}, { headers });
      toast.success('Firmenprofil gespeichert');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Speichern'); }
    finally { setFirmSaving(null); }
  };

  const handleHardwareExport = async () => {
    setExportingPdf(true);
    try {
      let url = `${API}/admin/employees/hardware-pdf?filter_type=${hwFilter}`;
      if (hwFilter === 'selection' && hwSelection.length > 0) {
        url += `&ids=${hwSelection.join(',')}`;
      }
      const res = await axios.get(url, { headers, responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      link.download = `hardware_liste_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      toast.success('Hardware-Liste exportiert');
      setShowHWDialog(false);
    } catch { toast.error('Fehler beim PDF-Export'); }
    finally { setExportingPdf(false); }
  };

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-0">
        {/* Header */}
        <div className="px-4 md:px-6 pt-6 pb-4 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">Personal</h1>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 hidden sm:block">Mitarbeiterverwaltung · Personalbögen · Verträge</p>
            </div>
            {activeTab === 'mitarbeiter' && (
              <div className="flex items-center gap-1.5 shrink-0">
                <Button variant="outline" size="sm" onClick={() => { setHwFilter('all'); setHwSelection([]); setShowHWDialog(true); }} className="gap-1.5 px-2 sm:px-3" data-testid="hardware-export-btn">
                  <Package className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">Hardware-Liste</span>
                </Button>
                <div className="relative">
                  <Button size="sm" onClick={() => setShowNewMenu(v => !v)} className="gap-1.5 px-2 sm:px-3">
                    <Plus className="h-4 w-4 shrink-0" />
                    <span className="hidden sm:inline">Neuer Mitarbeiter</span>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                {showNewMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowNewMenu(false)} />
                    <div className="absolute right-0 mt-2 w-64 bg-card border border-border rounded-xl shadow-xl z-20 overflow-hidden">
                      <button className="w-full flex items-start gap-3 px-4 py-3.5 hover:bg-muted transition-colors text-left"
                        onClick={() => { setShowNewMenu(false); navigate('/admin/employees'); }}>
                        <Users className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        <div><p className="text-sm font-semibold">Einfach anlegen</p>
                        <p className="text-xs text-muted-foreground">Name, E-Mail, Rolle – sofort</p></div>
                      </button>
                      <div className="border-t border-border" />
                      <button className="w-full flex items-start gap-3 px-4 py-3.5 hover:bg-muted transition-colors text-left"
                        onClick={() => { setShowNewMenu(false); setShowWizard(true); }}>
                        <ClipboardList className="h-5 w-5 text-violet-600 shrink-0 mt-0.5" />
                        <div><p className="text-sm font-semibold">Digitaler Personalbogen</p>
                        <p className="text-xs text-muted-foreground">Personalbogen per Link</p></div>
                      </button>
                      <div className="border-t border-border" />
                      {/* Blanko-Personalbogen herunterladen */}
                      <button className="w-full flex items-start gap-3 px-4 py-3.5 hover:bg-muted transition-colors text-left"
                        onClick={async () => {
                          setShowNewMenu(false);
                          try {
                            const res = await axios.get(`${API}/personalbogen/blank-pdf`, {
                              headers, responseType: 'blob'
                            });
                            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'Personalbogen_Blanko.pdf';
                            a.click();
                          } catch { toast.error('Blanko-PDF konnte nicht generiert werden'); }
                        }}
                        data-testid="download-blank-pb-btn"
                      >
                        <FileDown className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold">Blanko-Personalbogen</p>
                          <p className="text-xs text-muted-foreground">Leeres Formular als PDF drucken & ausfüllen</p>
                        </div>
                      </button>
                      <div className="border-t border-border" />
                      {/* RV-Befreiungsantrag Blanko */}
                      <button className="w-full flex items-start gap-3 px-4 py-3.5 hover:bg-muted transition-colors text-left"
                        onClick={async () => {
                          setShowNewMenu(false);
                          try {
                            const res = await axios.get(`${API}/personalbogen/rv-antrag-blank-pdf`, {
                              headers, responseType: 'blob'
                            });
                            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'RV_Befreiungsantrag_Blanko.pdf';
                            a.click();
                          } catch { toast.error('RV-Antrag konnte nicht generiert werden'); }
                        }}
                        data-testid="download-blank-rv-btn"
                      >
                        <Shield className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold">Blanko RV-Befreiungsantrag</p>
                          <p className="text-xs text-muted-foreground">Minijob · § 6 Abs. 1b SGB VI · zum Drucken & Ausfüllen</p>
                        </div>
                      </button>
                    </div>
                  </>
                )}
              </div>
              </div>
            )}
            {activeTab === 'vertraege' && (
              <Button size="sm" onClick={openDirectContract} className="gap-1.5 px-2 sm:px-3 shrink-0" data-testid="create-direct-contract-btn">
                <Plus className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">Vertrag erstellen</span>
              </Button>
            )}
            {activeTab === 'vorlagen' && templates.length === 0 && (
              <Button variant="outline" size="sm" onClick={handleSeedTemplates} className="shrink-0 text-xs px-2 sm:px-3">
                <span className="hidden sm:inline">Start-Templates einspielen</span>
                <span className="sm:hidden">Templates</span>
              </Button>
            )}
            {activeTab === 'vorlagen' && (
              <Button size="sm" onClick={() => navigate('/admin/vertragsvorlagen')} className="shrink-0 gap-1.5 px-2 sm:px-3">
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Neue Vorlage</span>
              </Button>
            )}
          </div>

          {/* Tab-Bar – horizontal scrollable on mobile */}
          <div className="w-full overflow-x-auto scrollbar-hide -mx-1 px-1">
            <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-max min-w-full sm:w-fit">
              {TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const badge = tab.id === 'onboarding' ? pendingBoqens.length
                            : tab.id === 'vertraege' ? pendingSignatures
                            : tab.id === 'mitarbeiter' ? employees.length : null;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setTab(tab.id)}
                    className={`flex items-center gap-1.5 px-2.5 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                      isActive ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
                    {tab.label}
                    {badge > 0 && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                        isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                      }`}>{badge}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-4 md:px-6 pb-8">

          {/* ─── TAB: MITARBEITER ──────────────────────────────────────────── */}
          {activeTab === 'mitarbeiter' && (
            <>
            {locations.length > 1 && (
              <div className="flex items-center gap-1.5 flex-wrap mb-1" data-testid="emp-location-filter-bar">
                <span className="text-xs text-muted-foreground mr-0.5">Standort:</span>
                <button onClick={() => setEmpLocationFilter('all')} data-testid="emp-location-filter-all"
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${empLocationFilter === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                  Alle ({employees.length})
                </button>
                {locations.map(loc => {
                  const cnt = employees.filter(e => (e.location_ids || []).includes(loc.id)).length;
                  return (
                    <button key={loc.id} onClick={() => setEmpLocationFilter(loc.id)} data-testid={`emp-location-filter-${loc.id}`}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${empLocationFilter === loc.id ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                      {loc.name || loc.city} ({cnt})
                    </button>
                  );
                })}
              </div>
            )}
            <div className="space-y-3">
              {loading.employees ? (
                <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : employees.length === 0 ? (
                <Card className="border-dashed"><CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">Noch keine Mitarbeiter</p>
                </CardContent></Card>
              ) : (() => {
                const visibleEmployees = empLocationFilter === 'all'
                  ? employees
                  : employees.filter(e => (e.location_ids || []).includes(empLocationFilter));
                if (visibleEmployees.length === 0) return (
                  <Card className="border-dashed"><CardContent className="py-10 text-center">
                    <p className="text-sm text-muted-foreground" data-testid="emp-location-empty">Keine Mitarbeiter an diesem Standort</p>
                  </CardContent></Card>
                );
                return visibleEmployees.map(emp => (
                  <Card key={emp.id} className="shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/admin/personal/mitarbeiter/${emp.id}`)}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${
                          emp.role === 'admin' ? 'bg-blue-500' : emp.role === 'stationsleitung' ? 'bg-violet-500' : 'bg-gray-400'
                        }`}>
                          {(emp.name || emp.email || '?')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <p className="font-semibold text-foreground truncate text-sm">{emp.name || emp.email}</p>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full shrink-0 ${emp.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {emp.is_active !== false ? 'Aktiv' : 'Inaktiv'}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{emp.email} · {emp.role}</p>
                          {locations.length > 1 && (emp.location_ids || []).length > 0 && (
                            <p className="text-[11px] text-muted-foreground/80 truncate flex items-center gap-1 mt-0.5" data-testid={`emp-station-${emp.id}`}>
                              <Building2 className="h-3 w-3 shrink-0" />
                              {(emp.location_ids || []).map(id => { const l = locations.find(x => x.id === id); return l?.name || l?.city; }).filter(Boolean).join(', ')}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-0.5 shrink-0">
                          {/* Vertrag-Button */}
                          {(() => {
                            const empBogen = personalboqens.find(b =>
                              b.employee_id === emp.id || b.user_id === emp.id ||
                              (b.personal?.email && emp.email && b.personal.email.toLowerCase() === emp.email.toLowerCase())
                            );
                            const hasSubmittedBogen = empBogen?.status === 'submitted' || empBogen?.status === 'admin_filled';
                            const hasContract = !!empBogen?.contract_signing_id;
                            // Kann immer neuen Vertrag erstellen wenn Bogen submitted (auch nach Löschung)
                            const canCreateContract = hasSubmittedBogen;
                            return (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                title={canCreateContract ? 'Vertrag erstellen' : hasContract ? 'Vertrag ansehen' : 'Direktvertrag erstellen'}
                                onClick={e => {
                                  e.stopPropagation();
                                  if (canCreateContract) {
                                    setContractCreateBogen(empBogen);
                                  } else if (hasContract) {
                                    setTab('vertraege');
                                  } else {
                                    openDirectContract();
                                    dcSelectEmployee(emp.id);
                                  }
                                }}
                                data-testid={`emp-contract-btn-${emp.id}`}
                              >
                                <FileText className={`h-4 w-4 ${hasContract ? 'text-green-600' : canCreateContract ? 'text-primary' : 'text-muted-foreground'}`} />
                              </Button>
                            );
                          })()}
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            onClick={e => { e.stopPropagation(); navigate(`/admin/personal/mitarbeiter/${emp.id}`); }}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                            onClick={e => { e.stopPropagation(); handleDeleteEmployee(emp); }}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ));
              })()}
            </div>

            {/* ── Archiv-Bereich ──────────────────────────────────────────── */}
            <div className="mt-4">
              <button
                onClick={() => { setShowArchive(v => !v); if (!showArchive) fetchArchivedEmployees(); }}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-2 px-3 rounded-lg hover:bg-muted/40 w-full sm:w-auto"
                data-testid="toggle-archive-btn"
              >
                <Archive className="h-4 w-4" />
                {showArchive ? 'Archiv ausblenden' : `Archiv anzeigen${archivedEmployees.length > 0 ? ` (${archivedEmployees.length})` : ''}`}
              </button>

              {showArchive && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-muted-foreground px-1">
                    Archivierte Mitarbeiter zählen nicht für die Abrechnung und können sich nicht einloggen.
                  </p>
                  {archivedEmployees.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">Keine archivierten Mitarbeiter</p>
                  ) : archivedEmployees.map(emp => (
                    <Card key={emp.id} className="border-dashed border-gray-300 dark:border-gray-700 opacity-75">
                      <CardContent className="p-3 space-y-2">
                        {/* Zeile 1: Avatar + Name + Datum */}
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 text-sm font-bold shrink-0">
                            {(emp.name || emp.email || '?')[0].toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-muted-foreground">{emp.name || emp.email}</p>
                            <p className="text-xs text-muted-foreground">{emp.email}</p>
                            {emp.archived_at && (
                              <p className="text-xs text-muted-foreground">
                                Archiviert: {new Date(emp.archived_at).toLocaleDateString('de-DE')}
                              </p>
                            )}
                          </div>
                        </div>
                        {/* Zeile 2: Aktionen – eigene Zeile, kein Überlappen */}
                        <div className="flex flex-wrap gap-1.5">
                          <Button variant="outline" size="sm" className="h-7 text-xs px-2"
                            onClick={() => navigate(`/admin/personal/mitarbeiter/${emp.id}`)}
                            data-testid={`archived-view-${emp.id}`}>
                            Anzeigen
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 text-xs px-2 text-green-600 border-green-300 hover:bg-green-50"
                            onClick={() => handleRestoreEmployee(emp)}
                            data-testid={`restore-emp-${emp.id}`}>
                            Wiederherstellen
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 text-xs px-2 text-destructive hover:bg-destructive/10"
                            onClick={() => setArchiveDialog({ emp, step: 'delete_confirm_archived' })}
                            data-testid={`perm-delete-${emp.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
            </>
          )}

          {/* ─── TAB: ONBOARDING ───────────────────────────────────────────── */}
          {activeTab === 'onboarding' && (
            <div className="space-y-5">
              {/* Aktive Bögen */}
              {pendingBoqens.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-violet-600" />
                    Aktive Vorgänge ({pendingBoqens.length})
                  </h3>
                  {pendingBoqens.map(bogen => {
                    const pe = bogen.personal || {};
                    const ag = bogen.arbeitgeber || {};
                    const displayName = (pe.vorname || pe.familienname)
                      ? `${pe.vorname || ''} ${pe.familienname || ''}`.trim()
                      : ag.berufsbezeichnung || 'Neuer Mitarbeiter';
                    const publicLink = `${appBase}/personalbogen/${bogen.token}`;
                    const sCfg = BOGEN_STATUS[bogen.status] || BOGEN_STATUS.draft;
                    const isMinijob = bogen.is_minijob ?? (bogen.beschaeftigung?.arbeitszeit === 'minijob' || bogen.arbeitgeber?.arbeitszeit === 'minijob');
                    const rvMissing = (bogen.rv_required ?? isMinijob) && !bogen.rv_complete;
                    return (
                      <Card key={bogen.id} className="border-l-4 border-l-violet-400 shadow-sm">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold text-sm text-foreground">{displayName}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {ag.berufsbezeichnung || '–'} · Eintritt: {formatDate(ag.eintrittsdatum)}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sCfg.cls}`}>{sCfg.label}</span>
                              {rvMissing && (
                                <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-red-100 text-red-700 border border-red-300 flex items-center gap-1"
                                  data-testid={`rv-missing-badge-${bogen.id}`} title="Rentenversicherungs-Befreiung unvollständig">
                                  <AlertCircle className="h-3 w-3" /> RV-Befreiung fehlt
                                </span>
                              )}
                              {rvMissing && bogen.rv_employee_done && !bogen.rv_employer_done && (
                                <button
                                  onClick={() => openRvSign(bogen)}
                                  data-testid={`rv-sign-ag-btn-${bogen.id}`}
                                  className="text-xs px-2 py-0.5 rounded-full font-semibold bg-violet-600 text-white hover:bg-violet-700 flex items-center gap-1"
                                  title="RV-Befreiung als Arbeitgeber gegenzeichnen">
                                  <Edit2 className="h-3 w-3" /> RV gegenzeichnen
                                </button>
                              )}
                            </div>
                          </div>
                          {bogen.pin_code && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                              <span>🔑 PIN: <span className="font-mono font-semibold text-foreground">{bogen.pin_code}</span></span>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {bogen.status !== 'approved' && (
                              <button onClick={() => { navigator.clipboard.writeText(publicLink); toast.success('Link kopiert!'); }}
                                className="flex items-center gap-1 text-xs bg-muted hover:bg-muted/80 px-2 py-1 rounded-md">
                                <LinkIcon className="h-3 w-3" /> Link
                              </button>
                            )}
                          {/* E-Mail-Versand Button – wenn Bogen auf Mitarbeiter wartet */}
                          {bogen.status !== 'submitted' && bogen.status !== 'approved' && bogen.status !== 'admin_filled' && bogen.mitarbeiter_email && (
                            <button
                              onClick={() => handleSendBogenEmail(bogen)}
                              className="flex items-center gap-1 text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 px-2 py-1 rounded-md"
                              title={`PIN-Link an ${bogen.mitarbeiter_email} senden`}
                              data-testid={`send-email-btn-${bogen.id}`}
                            >
                              <Mail className="h-3 w-3" /> E-Mail
                            </button>
                          )}
                          {(bogen.status === 'submitted' || bogen.status === 'admin_filled') && (
                              <>
                                <button onClick={() => downloadPDF(`${API}/personalbogen/${bogen.id}/pdf-form`, `Personalbogen_${displayName}.pdf`)}
                                  className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 px-2 py-1 rounded-md">
                                  <FileDown className="h-3 w-3" /> PDF
                                </button>
                                <button onClick={() => downloadPDF(`${API}/personalbogen/${bogen.id}/full-combined-pdf`, `Personalakte_${displayName}.pdf`)}
                                  className="flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 hover:bg-indigo-200 px-2 py-1 rounded-md">
                                  <FileDown className="h-3 w-3" /> Akte
                                </button>
                                {/* Vertrag erstellen / anzeigen */}
                                {!bogen.contract_signing_id ? (
                                  <button
                                    onClick={() => setContractCreateBogen(bogen)}
                                    className="flex items-center gap-1 text-xs bg-primary/10 text-primary hover:bg-primary/20 px-2 py-1 rounded-md font-semibold"
                                    data-testid={`create-contract-btn-${bogen.id}`}
                                  >
                                    <FileText className="h-3 w-3" /> Vertrag erstellen
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => setTab('vertraege')}
                                    className="flex items-center gap-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 px-2 py-1 rounded-md font-medium"
                                    data-testid={`view-contract-btn-${bogen.id}`}
                                  >
                                    <FileText className="h-3 w-3" /> Vertrag ansehen
                                  </button>
                                )}
                                {/* Account erstellen – NUR für neue Mitarbeiter (nicht für bestehende mit vorhandener employee_id oder user_id) */}
                                {!bogen.employee_id && !bogen.user_id && (
                                <button onClick={() => handleCreateAccount(bogen)}
                                  className="flex items-center gap-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 px-2 py-1 rounded-md">
                                  <Users className="h-3 w-3" /> Account
                                </button>
                                )}
                              </>
                            )}
                            {rvMissing && (
                              <button onClick={() => setRvNachholenBogen(bogen)}
                                className="flex items-center gap-1 text-xs bg-red-100 text-red-700 hover:bg-red-200 px-2 py-1 rounded-md font-semibold"
                                data-testid={`rv-nachholen-btn-${bogen.id}`}>
                                <Shield className="h-3 w-3" /> RV nachholen
                              </button>
                            )}
                            <button onClick={() => setEditingBogen(bogen)}
                              className="flex items-center gap-1 text-xs bg-violet-100 text-violet-700 hover:bg-violet-200 px-2 py-1 rounded-md font-medium">
                              <Edit2 className="h-3 w-3" /> Bearbeiten
                            </button>
                            <button onClick={() => handleDeleteBogen(bogen.id)}
                              className="flex items-center gap-1 text-xs bg-red-50 text-red-600 hover:bg-red-100 px-2 py-1 rounded-md">
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Archiv */}
              {archivedBoqens.length > 0 && (
                <details className="space-y-2">
                  <summary className="flex items-center gap-2 cursor-pointer list-none text-sm font-semibold text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    Abgeschlossen ({archivedBoqens.length})
                  </summary>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {archivedBoqens.map(bogen => {
                      const pe = bogen.personal || {};
                      const ag = bogen.arbeitgeber || {};
                      const displayName = `${pe.vorname || ''} ${pe.familienname || ''}`.trim() || ag.berufsbezeichnung || 'Mitarbeiter';
                      const isMinijob = bogen.is_minijob ?? (bogen.beschaeftigung?.arbeitszeit === 'minijob' || bogen.arbeitgeber?.arbeitszeit === 'minijob');
                      const rvMissing = (bogen.rv_required ?? isMinijob) && !bogen.rv_complete;
                      return (
                        <div key={bogen.id} className={`flex items-center justify-between gap-2 p-3 rounded-lg border ${rvMissing ? 'border-red-300 bg-red-50/60 dark:bg-red-900/10' : 'border-green-200 bg-green-50/50 dark:bg-green-900/10'}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{displayName}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(ag.eintrittsdatum)}</p>
                            {rvMissing && (
                              <span className="mt-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold bg-red-100 text-red-700 border border-red-300"
                                data-testid={`rv-missing-badge-${bogen.id}`}>
                                <AlertCircle className="h-3 w-3" /> RV-Befreiung fehlt
                              </span>
                            )}
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <button onClick={() => downloadPDF(`${API}/personalbogen/${bogen.id}/pdf-form`, `PB_${displayName}.pdf`)}
                              className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-md flex items-center gap-1">
                              <FileDown className="h-3 w-3" /> PDF
                            </button>
                            {isMinijob && (
                              <button onClick={() => downloadPDF(`${API}/personalbogen/${bogen.id}/full-combined-pdf`, `Akte_${displayName}.pdf`)}
                                className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-md flex items-center gap-1">
                                <FileDown className="h-3 w-3" /> Akte
                              </button>
                            )}
                            <button onClick={() => setEditingBogen(bogen)}
                              className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-md">
                              <Edit2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}

              {pendingBoqens.length === 0 && archivedBoqens.length === 0 && (
                <Card className="border-dashed"><CardContent className="py-12 text-center">
                  <ClipboardList className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">Noch keine Personalbögen vorhanden</p>
                  <Button className="mt-3" size="sm" onClick={() => setShowWizard(true)}>
                    <Plus className="h-4 w-4 mr-1" /> Ersten Bogen starten
                  </Button>
                </CardContent></Card>
              )}
            </div>
          )}

          {/* ─── TAB: VERTRÄGE ─────────────────────────────────────────────── */}
          {activeTab === 'vertraege' && (
            <div className="space-y-3">
              {loading.signings ? (
                <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : signings.length === 0 ? (
                <Card className="border-dashed"><CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">Noch keine Verträge</p>
                </CardContent></Card>
              ) : (
                signings.map(s => {
                  const vars = s.variables_used || {};
                  const name = `${vars.vorname || ''} ${vars.nachname || ''}`.trim() || s.employee_name || 'Mitarbeiter';
                  const sCfg = SIGNING_STATUS[s.status] || SIGNING_STATUS.draft;
                  const isExp = expandedSigning === s.id;
                  const formatDt = (iso) => { try { return new Date(iso).toLocaleDateString('de-DE', { day:'2-digit', month:'2-digit', year:'2-digit' }); } catch { return '–'; } };

                  const handleDelete = async (e) => {
                    e.stopPropagation();
                    if (!window.confirm(`Vertrag von ${name} wirklich löschen?`)) return;
                    try {
                      await axios.delete(`${API}/contracts/signing/${s.id}`, { headers });
                      toast.success('Vertrag gelöscht');
                      fetchAll();
                    } catch { toast.error('Fehler beim Löschen'); }
                  };

                  const handleGenSigningLink = async (e) => {
                    e.stopPropagation();
                    try {
                      const res = await axios.post(`${API}/contracts/signing/${s.id}/generate-signing-link`, {}, { headers });
                      const fullUrl = `${window.location.origin}${res.data.url}`;
                      // Zeige Dialog mit Link + PIN statt nur Toast
                      setSigningLinkInfo({ url: fullUrl, pin_code: res.data.pin_code, name, signing: s });
                    } catch { toast.error('Fehler beim Erstellen des Links'); }
                  };

                  return (
                    <Card key={s.id} className="shadow-sm">
                      <CardContent className="p-0">
                        <button className="w-full p-4 text-left" onClick={() => setExpandedSigning(isExp ? null : s.id)}>
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-primary shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-sm">{name}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sCfg.cls}`}>{sCfg.label}</span>
                                {s.is_direct && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">Direktvertrag</span>}
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">{s.template_name} · {formatDt(s.created_at)}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {/* AG unterschreiben – sichtbar in kompakter Karte wenn noch nicht unterschrieben */}
                              {s.status === 'draft' && !s.signature_ag && (
                                <Button size="sm" onClick={e => {
                                  e.stopPropagation();
                                  setAgDatum(new Date().toLocaleDateString('de-DE'));
                                  setAgOrt('');
                                  setShowSignAG(s);
                                }}
                                  className="gap-1 bg-green-600 hover:bg-green-700 text-white text-xs"
                                  data-testid={`quick-sign-ag-btn-${s.id}`}>
                                  <Edit2 className="h-3 w-3" /> AG unterschreiben
                                </Button>
                              )}
                              {s.status === 'signed_an' && (
                                <Button size="sm" onClick={e => { e.stopPropagation(); navigate('/admin/vertraege'); }}
                                  className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs">
                                  <Edit2 className="h-3 w-3" /> Gegenzeichnen
                                </Button>
                              )}
                              {/* Schnellbutton direkt auf der Karte (Vollständig) */}
                              {s.status === 'complete' && (
                                <Button size="sm" variant="outline"
                                  className="gap-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-xs hidden sm:flex"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await axios.post(`${API}/contracts/signing/${s.id}/resend-completed-email`, {}, { headers });
                                      toast.success(`Verschlüsseltes PDF an ${name} gesendet!`);
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Fehler');
                                    }
                                  }}
                                  data-testid={`quick-send-btn-${s.id}`}
                                >
                                  <Mail className="h-3 w-3" /> 🔒 Senden
                                </Button>
                              )}
                              {isExp ? <Eye className="h-4 w-4 text-muted-foreground" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                            </div>
                          </div>
                        </button>
                        {isExp && (
                          <div className="border-t px-4 pb-4 pt-3 space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                              <div className={`rounded-lg p-2.5 text-xs ${s.signature_an && s.signature_an !== 'already_signed' ? 'bg-green-50 border border-green-200' : 'bg-muted/40'}`}>
                                <p className="font-medium flex items-center gap-1">
                                  {s.signature_an && s.signature_an !== 'already_signed' ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <Clock className="h-3.5 w-3.5 text-muted-foreground" />}
                                  Arbeitnehmer
                                </p>
                                <p className="text-muted-foreground mt-0.5">{s.signed_datum_an || (s.signed_at_an ? formatDt(s.signed_at_an) : 'Ausstehend')}</p>
                              </div>
                              <div className={`rounded-lg p-2.5 text-xs ${s.signature_ag ? 'bg-green-50 border border-green-200' : 'bg-muted/40'}`}>
                                <p className="font-medium flex items-center gap-1">
                                  {s.signature_ag ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <Clock className="h-3.5 w-3.5 text-muted-foreground" />}
                                  Arbeitgeber
                                </p>
                                <p className="text-muted-foreground mt-0.5">{s.signed_datum_ag || (s.signed_at_ag ? formatDt(s.signed_at_ag) : 'Ausstehend')}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button onClick={() => downloadPDF(`${API}/contracts/signing/${s.id}/pdf`, `Vertrag_${name}.pdf`)}
                                className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 px-3 py-1.5 rounded-md">
                                <FileDown className="h-3.5 w-3.5" /> Vertrag PDF
                              </button>

                              {/* Feature B+C: Verschlüsselt senden – prominent für "complete" Verträge */}
                              {s.status === 'complete' && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await axios.post(`${API}/contracts/signing/${s.id}/resend-completed-email`, {}, { headers });
                                      toast.success(`Verschlüsseltes PDF wurde an ${name} gesendet! Passwort = persönlicher PIN-Code.`);
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Fehler beim Senden');
                                    }
                                  }}
                                  className="flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 hover:bg-indigo-200 px-3 py-1.5 rounded-md font-medium"
                                  data-testid={`resend-email-btn-${s.id}`}
                                  title="PDF wird verschlüsselt (Passwort = PIN-Code des Mitarbeiters)"
                                >
                                  <Mail className="h-3.5 w-3.5" /> 🔒 Verschlüsselt an MA senden
                                </button>
                              )}

                              {/* AG digital unterschreiben – wenn noch keine echte AG-Signatur */}
                              {(!s.signature_ag || s.signature_ag === 'already_signed') && (
                                <button
                                  onClick={() => {
                                    setAgDatum(new Date().toLocaleDateString('de-DE'));
                                    setAgOrt('');
                                    setShowSignAG(s);
                                  }}
                                  className="flex items-center gap-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 px-3 py-1.5 rounded-md"
                                  data-testid={`sign-ag-btn-${s.id}`}
                                >
                                  <Edit2 className="h-3.5 w-3.5" /> AG unterschreiben
                                </button>
                              )}

                              {/* Signierlink für ALLE Verträge wo AN noch nicht unterschrieben hat */}
                              {(!s.signature_an || s.signature_an === 'already_signed') && (
                                <button onClick={handleGenSigningLink}
                                  className="flex items-center gap-1 text-xs bg-amber-100 text-amber-700 hover:bg-amber-200 px-3 py-1.5 rounded-md"
                                  data-testid={`signing-link-btn-${s.id}`}>
                                  <LinkIcon className="h-3.5 w-3.5" /> Signierlink erstellen & senden
                                </button>
                              )}

                              {/* Feature A: Vertrag korrigieren – für alle nicht-entwurf Verträge */}
                              {s.status !== 'draft' && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (!window.confirm(`Vertrag für "${name}" wirklich zurücksetzen?\n\nAlle bestehenden Signaturen werden gelöscht. Der Signierlink muss neu erstellt werden.`)) return;
                                    try {
                                      await axios.post(`${API}/contracts/signing/${s.id}/reset-to-draft`, {}, { headers });
                                      toast.success('Vertrag zurückgesetzt. Bitte Signierlink neu erstellen.');
                                      fetchAll();
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Fehler beim Zurücksetzen');
                                    }
                                  }}
                                  className="flex items-center gap-1 text-xs bg-orange-100 text-orange-700 hover:bg-orange-200 px-3 py-1.5 rounded-md border border-orange-200"
                                  data-testid={`reset-draft-btn-${s.id}`}
                                  title="Signaturen löschen und Vertrag zur Korrektur freigeben"
                                >
                                  <RefreshCw className="h-3.5 w-3.5" /> Vertrag korrigieren
                                </button>
                              )}

                              <button onClick={handleDelete}
                                className="flex items-center gap-1 text-xs bg-red-100 text-red-700 hover:bg-red-200 px-3 py-1.5 rounded-md"
                                data-testid={`delete-signing-btn-${s.id}`}>
                                <Trash2 className="h-3.5 w-3.5" /> Löschen
                              </button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          )}

          {/* ─── TAB: VERTRAGSVORLAGEN ─────────────────────────────────────── */}
          {activeTab === 'vorlagen' && (
            <div className="space-y-3">
              {loading.templates ? (
                <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : templates.length === 0 ? (
                <Card className="border-dashed"><CardContent className="py-12 text-center space-y-3">
                  <Shield className="h-12 w-12 text-muted-foreground/30 mx-auto" />
                  <p className="text-muted-foreground">Noch keine Vertragsvorlagen</p>
                  <Button variant="outline" onClick={handleSeedTemplates}>Start-Templates einspielen</Button>
                </CardContent></Card>
              ) : (
                templates.map(t => (
                  <Card key={t.id} className="shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-foreground">{t.name}</h3>
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                              {CONTRACT_TYPES[t.contract_type] || t.contract_type}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 truncate">{t.template_text.slice(0, 100)}…</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => setPreviewTemplate(previewTemplate === t.id ? null : t.id)}>
                            {previewTemplate === t.id ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => navigate(`/admin/vertragsvorlagen`)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                            onClick={() => handleDeleteTemplate(t)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {previewTemplate === t.id && (
                        <div className="mt-3 bg-muted/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                          <pre className="text-xs text-foreground whitespace-pre-wrap font-sans">{t.template_text}</pre>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
          {/* ─── TAB: BETRIEBSVEREINBARUNGEN ──────────────────────────────── */}
          {activeTab === 'betriebsvereinbarungen' && (
            <BetriebsvereinbarungenPage employees={employees} />
          )}

          {/* ─── TAB: DOK.-ORDNER ──────────────────────────────────────────── */}
          {activeTab === 'dok-ordner' && (
            <DokumentOrdnerVerwaltung isAdmin={user?.role === 'admin'} />
          )}

          {/* ─── TAB: FIRMENPROFIL ─────────────────────────────────────────── */}
          {activeTab === 'firmenprofil' && (
            <div className="space-y-4">
              {/* Info-Box mit Link */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">Rechtliche Firmendaten für Verträge</p>
                      <p className="text-xs text-blue-600 dark:text-blue-300 mt-0.5 leading-relaxed">
                        Diese Daten erscheinen in Vertragsvorlagen als Variablen wie{' '}
                        <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">&#123;firma_name&#125;</code>,{' '}
                        <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">&#123;firma_unterzeichner&#125;</code>,{' '}
                        <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">&#123;firma_betriebsnummer&#125;</code> usw.
                        Für jeden Standort separat ausfüllbar.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/admin/locations')}
                    className="shrink-0 text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-medium"
                    data-testid="goto-locations-btn"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Standort hinzufügen
                  </button>
                </div>
              </div>

              {/* Verfügbare Variablen – Übersicht */}
              <div className="bg-muted/40 border rounded-xl p-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Verfügbare Variablen für Vertragsvorlagen</p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    '{firma_name}', '{firma_inhaber}', '{firma_adresse}', '{firma_plz_ort}',
                    '{firma_unterzeichner}', '{firma_betriebsnummer}', '{firma_steuernummer}',
                    '{firma_ust_id}', '{firma_registergericht}', '{firma_hrb}', '{standort}'
                  ].map(v => (
                    <code key={v} className="text-[10px] bg-background border px-1.5 py-0.5 rounded font-mono text-primary">{v}</code>
                  ))}
                </div>
              </div>

              {locations.length === 0 ? (
                <Card className="border-dashed"><CardContent className="py-12 text-center">
                  <Building2 className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">Keine Standorte gefunden</p>
                  <button onClick={() => navigate('/admin/locations')}
                    className="mt-3 text-sm text-primary hover:underline">
                    Ersten Standort anlegen →
                  </button>
                </CardContent></Card>
              ) : (
                locations.map(loc => {
                  const form = firmForms[loc.id] || {};
                  const hasProfile = !!(form.legal_name && form.legal_owner && form.legal_address && form.legal_plz_ort);
                  const ff = (field) => (e) => setFirmForms(f => ({ ...f, [loc.id]: { ...f[loc.id], [field]: e.target.value } }));
                  return (
                    <Card key={loc.id} className="shadow-sm">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between gap-3 mb-5">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Building2 className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-semibold text-sm text-foreground">{loc.name || loc.city}</p>
                              <p className="text-xs text-muted-foreground">{loc.city}</p>
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            hasProfile ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                       : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                          }`}>
                            {hasProfile ? 'Vollständig' : 'Unvollständig'}
                          </span>
                        </div>

                        {/* Sektion 1: Grunddaten */}
                        <div className="space-y-3 mb-4">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Basisdaten für Vertragsheader</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Firmenname <code className="text-[10px] ml-1 opacity-60">&#123;firma_name&#125;</code></Label>
                              <input value={form.legal_name || ''} onChange={ff('legal_name')}
                                placeholder="z.B. JET Tankstellen GmbH" data-testid={`firm-name-${loc.id}`}
                                className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Inhaber / Vertreter <code className="text-[10px] ml-1 opacity-60">&#123;firma_inhaber&#125;</code></Label>
                              <input value={form.legal_owner || ''} onChange={ff('legal_owner')}
                                placeholder="z.B. Ali Tombul" data-testid={`firm-owner-${loc.id}`}
                                className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Straße + Hausnummer <code className="text-[10px] ml-1 opacity-60">&#123;firma_adresse&#125;</code></Label>
                              <input value={form.legal_address || ''} onChange={ff('legal_address')}
                                placeholder="z.B. Stuttgarter Str. 1" data-testid={`firm-address-${loc.id}`}
                                className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">PLZ / Ort <code className="text-[10px] ml-1 opacity-60">&#123;firma_plz_ort&#125;</code></Label>
                              <input value={form.legal_plz_ort || ''} onChange={ff('legal_plz_ort')}
                                placeholder="z.B. 70734 Fellbach" data-testid={`firm-plz-${loc.id}`}
                                className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                            </div>
                          </div>
                        </div>

                        {/* Sektion 2: Unterzeichner */}
                        <div className="space-y-3 mb-4 pt-4 border-t">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Unterzeichner (Vertragsunterschrift)</p>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">
                              Unterzeichner inkl. Titel <code className="text-[10px] ml-1 opacity-60">&#123;firma_unterzeichner&#125;</code>
                            </Label>
                            <input value={form.legal_unterzeichner || ''} onChange={ff('legal_unterzeichner')}
                              placeholder="z.B. Ali Tombul, Geschäftsführer" data-testid={`firm-unterzeichner-${loc.id}`}
                              className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                            <p className="text-xs text-muted-foreground">Erscheint im Vertrag als „vertreten durch …". Falls leer → Inhaber-Feld wird verwendet.</p>
                          </div>
                        </div>

                        {/* Sektion 3: Steuer & Betriebsnummer */}
                        <div className="space-y-3 mb-4 pt-4 border-t">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Steuer- & Sozialversicherungsdaten</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                Betriebsnummer <code className="text-[10px] ml-1 opacity-60">&#123;firma_betriebsnummer&#125;</code>
                              </Label>
                              <input value={form.legal_betriebsnummer || ''} onChange={ff('legal_betriebsnummer')}
                                placeholder="z.B. 12345678" data-testid={`firm-betriebsnummer-${loc.id}`}
                                className="w-full h-9 rounded-md border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary" />
                              <p className="text-xs text-muted-foreground">Wird automatisch in neue Minijob-RV-Anträge dieses Standorts übernommen.</p>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                Steuernummer <code className="text-[10px] ml-1 opacity-60">&#123;firma_steuernummer&#125;</code>
                              </Label>
                              <input value={form.legal_steuernummer || ''} onChange={ff('legal_steuernummer')}
                                placeholder="z.B. 12/345/67890" data-testid={`firm-steuernummer-${loc.id}`}
                                className="w-full h-9 rounded-md border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                USt-IdNr. <code className="text-[10px] ml-1 opacity-60">&#123;firma_ust_id&#125;</code>
                              </Label>
                              <input value={form.legal_ust_id || ''} onChange={ff('legal_ust_id')}
                                placeholder="z.B. DE123456789" data-testid={`firm-ust-${loc.id}`}
                                className="w-full h-9 rounded-md border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary" />
                            </div>
                          </div>
                        </div>

                        {/* Sektion 4: Handelsregister (optional) */}
                        <div className="space-y-3 mb-5 pt-4 border-t">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            Handelsregister <span className="font-normal normal-case">(optional – nur für GmbH/AG)</span>
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                Registergericht <code className="text-[10px] ml-1 opacity-60">&#123;firma_registergericht&#125;</code>
                              </Label>
                              <input value={form.legal_registergericht || ''} onChange={ff('legal_registergericht')}
                                placeholder="z.B. Amtsgericht Stuttgart" data-testid={`firm-gericht-${loc.id}`}
                                className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                HRB-Nummer <code className="text-[10px] ml-1 opacity-60">&#123;firma_hrb&#125;</code>
                              </Label>
                              <input value={form.legal_hrb || ''} onChange={ff('legal_hrb')}
                                placeholder="z.B. HRB 12345" data-testid={`firm-hrb-${loc.id}`}
                                className="w-full h-9 rounded-md border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary" />
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <Button onClick={() => handleFirmSave(loc.id)} disabled={firmSaving === loc.id}
                            size="sm" className="gap-2" data-testid={`firm-save-${loc.id}`}>
                            {firmSaving === loc.id
                              ? <><Loader2 className="h-4 w-4 animate-spin" /> Speichert…</>
                              : <><Save className="h-4 w-4" /> Speichern</>}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Wizard + Edit Modal */}
      <PersonalboqenWizard
        open={showWizard}
        onClose={() => setShowWizard(false)}
        onCreated={(bogen) => { fetchAll(); if (bogen) setEditingBogen(bogen); }}
        locations={locations}
      />
      <PersonalboqenEditModal
        open={!!editingBogen}
        bogen={editingBogen}
        onClose={() => setEditingBogen(null)}
        onSaved={() => { fetchAll(); setEditingBogen(null); }}
      />

      <RvNachholenDialog
        bogen={rvNachholenBogen}
        open={!!rvNachholenBogen}
        onClose={() => setRvNachholenBogen(null)}
        onDone={() => fetchAll()}
        API={API}
        headers={headers}
      />

      {/* ── Direktvertrag Dialog ─────────────────────────────────────────── */}
      <Dialog open={showDirectContract} onOpenChange={setShowDirectContract}>
        <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Vertrag erstellen
              <span className="ml-auto text-xs font-normal text-muted-foreground">Schritt {dcStep} / 3</span>
            </DialogTitle>
            {/* Step indicator */}
            <div className="flex gap-1.5 mt-2">
              {[1,2,3].map(s => (
                <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= dcStep ? 'bg-primary' : 'bg-muted'}`} />
              ))}
            </div>
          </DialogHeader>

          {/* Step 1: Mitarbeiter + Vorlage + Standort */}
          {dcStep === 1 && (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Mitarbeiter *</Label>
                <select
                  value={dcEmployeeId}
                  onChange={e => dcSelectEmployee(e.target.value)}
                  className="w-full h-10 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="dc-employee-select"
                >
                  <option value="">– Mitarbeiter wählen –</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name || emp.email}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Vertragsvorlage *</Label>
                <select
                  value={dcTemplateId}
                  onChange={e => setDcTemplateId(e.target.value)}
                  className="w-full h-10 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="dc-template-select"
                >
                  <option value="">– Vorlage wählen –</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Standort (Firmendaten)</Label>
                <select
                  value={dcLocationId}
                  onChange={e => setDcLocationId(e.target.value)}
                  className="w-full h-10 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="dc-location-select"
                >
                  <option value="">– Standort wählen –</option>
                  {locations.map(loc => {
                    const hasProfile = !!(loc.legal_name && loc.legal_owner);
                    return (
                      <option key={loc.id} value={loc.id}>
                        {loc.name || loc.city}{hasProfile ? '' : ' (kein Firmenprofil)'}
                      </option>
                    );
                  })}
                </select>
                {dcLocationId && !locations.find(l => l.id === dcLocationId)?.legal_name && (
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    Dieser Standort hat noch kein Firmenprofil. Bitte unter "Firmenprofil" ausfüllen, damit die Vertragsdaten korrekt sind.
                  </p>
                )}
                {!dcLocationId && (
                  <p className="text-xs text-muted-foreground">Optional – ohne Auswahl werden Firmendaten automatisch geladen</p>
                )}
              </div>
              {templates.length === 0 && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Noch keine Vorlagen vorhanden. Bitte zuerst im Tab "Vertragsvorlagen" Vorlagen anlegen.
                </p>
              )}
            </div>
          )}

          {/* Step 2: Vertragsdaten */}
          {dcStep === 2 && (
            <div className="space-y-3 py-2">
              {/* Fix C: Template-Typ für bedingte Felder ermitteln */}
              {(() => {
                const selTmpl = templates.find(t => t.id === dcTemplateId);
                const tmplText = selTmpl?.template_text || '';
                const needsProbezeit = tmplText.includes('{{probezeit_dauer}}');
                const needsWerkstudent = tmplText.includes('{{hochschule}}');
                const needsZusatz = tmplText.includes('{{lohnerhoehung_datum}}');
                return (
              <p className="text-xs text-muted-foreground">Fülle die Vertragsdaten aus. Felder werden im Vertragstext als Variablen ersetzt.</p>
                );
              })()}
              <p className="text-xs text-muted-foreground">Felder werden automatisch aus dem Personalbogen übernommen – bitte prüfen und ggf. anpassen.</p>
              {(() => {
                const selTmpl = templates.find(t => t.id === dcTemplateId);
                const tmplText = selTmpl?.template_text || '';
                const needsProbezeit = tmplText.includes('{{probezeit_dauer}}');
                const needsWerkstudent = tmplText.includes('{{hochschule}}');
                const needsZusatz = tmplText.includes('{{lohnerhoehung_datum}}');
                return (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Vorname</Label>
                  <input value={dcVars.vorname} onChange={e => setDcVars(v => ({...v, vorname: e.target.value}))}
                    className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Nachname</Label>
                  <input value={dcVars.nachname} onChange={e => setDcVars(v => ({...v, nachname: e.target.value}))}
                    className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Eintrittsdatum</Label>
                  <input type="date" value={dcVars.eintrittsdatum} onChange={e => setDcVars(v => ({...v, eintrittsdatum: e.target.value}))}
                    className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Berufsbezeichnung / Tätigkeit</Label>
                  <input value={dcVars.job_bezeichnung} onChange={e => setDcVars(v => ({...v, job_bezeichnung: e.target.value, taetigkeit: e.target.value}))}
                    placeholder="z.B. Tankstellenservicekraft" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>

                {/* Probezeit – für alle Templates außer Zusatzvereinbarung */}
                {needsProbezeit && (
                  <div className="space-y-1.5 col-span-2">
                    <Label className="text-xs text-muted-foreground">Probezeit-Dauer <span className="text-muted-foreground/60 ml-1 text-xs">(für <code className="bg-muted px-1 rounded">&#123;&#123;probezeit_dauer&#125;&#125;</code>)</span></Label>
                    <input value={dcVars.probezeit_dauer} onChange={e => setDcVars(v => ({...v, probezeit_dauer: e.target.value}))}
                      placeholder="z.B. sechs Monate" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                )}

                {/* Stundenart Toggle + Eingabe (ganze Breite) */}
                {!needsZusatz && (
                <div className="col-span-2 space-y-2">
                  <Label className="text-xs text-muted-foreground">Arbeitszeit</Label>
                  <div className="flex rounded-lg border overflow-hidden">
                    {[
                      { key: 'wochenstunden', label: 'Wochenstunden' },
                      { key: 'monatsstunden', label: 'Monatsstunden' },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setDcVars(v => ({ ...v, _stundenart: opt.key }))}
                        className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                          (dcVars._stundenart || 'wochenstunden') === opt.key
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-background text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {(dcVars._stundenart || 'wochenstunden') === 'wochenstunden' ? (
                    <input
                      type="number"
                      value={dcVars.wochenstunden}
                      onChange={e => setDcVars(v => ({...v, wochenstunden: e.target.value, monatsstunden: ''}))}
                      placeholder="z.B. 40"
                      className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  ) : (
                    <input
                      type="number"
                      value={dcVars.monatsstunden}
                      onChange={e => setDcVars(v => ({...v, monatsstunden: e.target.value, wochenstunden: ''}))}
                      placeholder="z.B. 80"
                      className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  )}
                </div>
                )}

                {!needsZusatz && (<>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Urlaubstage / Jahr</Label>
                  <div className="space-y-1">
                    <input type="number" min="1" value={dcVars.urlaubstage} onChange={e => setDcVars(v => ({...v, urlaubstage: e.target.value}))}
                      placeholder="z.B. 20" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                    {dcVars.wochenstunden && !dcVars.urlaubstage && (
                      <p className="text-xs text-primary cursor-pointer hover:underline" onClick={() => setDcVars(v => ({...v, urlaubstage: String(Math.round(parseFloat(v.wochenstunden)/5*4) || 20)}))}>
                        Tipp: Stunden/Woche ÷ 5 × 4 = {Math.round(parseFloat(dcVars.wochenstunden)/5*4)} Tage auto berechnen
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Arbeitstage / Woche
                    <span className="text-muted-foreground/60 ml-1 text-xs font-normal">(für <code className="bg-muted px-1 rounded">&#123;&#123;arbeitstage_woche&#125;&#125;</code>)</span>
                  </Label>
                  <input type="number" min="1" max="7" value={dcVars.arbeitstage_woche} onChange={e => setDcVars(v => ({...v, arbeitstage_woche: e.target.value}))}
                    placeholder="z.B. 5" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="dc-arbeitstage-woche" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Stundenlohn (€)</Label>
                  <input type="number" step="0.01" value={dcVars.stundenlohn} onChange={e => setDcVars(v => ({...v, stundenlohn: e.target.value}))}
                    placeholder="z.B. 13.50" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Monatslohn (€)</Label>
                  <input type="number" step="0.01" value={dcVars.monatslohn} onChange={e => setDcVars(v => ({...v, monatslohn: e.target.value}))}
                    placeholder="z.B. 2200" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                </>)}

                {/* Befristung – für alle außer Zusatz */}
                {!needsZusatz && (
                  <div className="space-y-1.5 col-span-2">
                    <Label className="text-xs text-muted-foreground">Befristet bis <span className="text-muted-foreground/60 ml-1 text-xs">(leer = unbefristetes Arbeitsverhältnis)</span></Label>
                    <input type="date" value={dcVars.befristung_bis} onChange={e => setDcVars(v => ({...v, befristung_bis: e.target.value}))}
                      className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                )}

                {/* Werkstudent-spezifische Felder */}
                {needsWerkstudent && (
                  <div className="col-span-2 space-y-3 pt-2 border-t border-border/60">
                    <p className="text-xs font-medium text-foreground">Werkstudent-Daten</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Hochschule</Label>
                        <input value={dcVars.hochschule} onChange={e => setDcVars(v => ({...v, hochschule: e.target.value}))}
                          placeholder="z.B. Universität Stuttgart" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Studiengang</Label>
                        <input value={dcVars.studiengang} onChange={e => setDcVars(v => ({...v, studiengang: e.target.value}))}
                          placeholder="z.B. BWL" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Matrikelnummer</Label>
                        <input value={dcVars.matrikelnummer} onChange={e => setDcVars(v => ({...v, matrikelnummer: e.target.value}))}
                          placeholder="z.B. 123456" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Stundenlohn (€)</Label>
                        <input type="number" step="0.01" value={dcVars.stundenlohn} onChange={e => setDcVars(v => ({...v, stundenlohn: e.target.value}))}
                          placeholder="z.B. 14.00" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Max. Std./Woche (Vorlesungszeit)</Label>
                        <input type="number" value={dcVars.max_stunden_semester} onChange={e => setDcVars(v => ({...v, max_stunden_semester: e.target.value}))}
                          placeholder="z.B. 20" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Max. Std./Woche (Semesterferien)</Label>
                        <input type="number" value={dcVars.max_stunden_semesterferien} onChange={e => setDcVars(v => ({...v, max_stunden_semesterferien: e.target.value}))}
                          placeholder="z.B. 40" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Urlaubstage / Jahr <span className="text-muted-foreground/60 text-xs">(anteilig)</span></Label>
                        <input type="number" value={dcVars.urlaubstage} onChange={e => setDcVars(v => ({...v, urlaubstage: e.target.value}))}
                          placeholder="z.B. 20" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Arbeitstage / Woche</Label>
                        <input type="number" min="1" max="7" value={dcVars.arbeitstage_woche} onChange={e => setDcVars(v => ({...v, arbeitstage_woche: e.target.value}))}
                          placeholder="z.B. 5" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Zusatzvereinbarungs-Felder */}
                {needsZusatz && (
                  <div className="col-span-2 space-y-3 pt-2 border-t border-border/60">
                    <p className="text-xs font-medium text-foreground">Zusatzvereinbarung</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Gültig ab (Datum der Lohnerhöhung)</Label>
                        <input type="date" value={dcVars.lohnerhoehung_datum} onChange={e => setDcVars(v => ({...v, lohnerhoehung_datum: e.target.value}))}
                          className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Neuer Stundenlohn (€)</Label>
                        <input type="number" step="0.01" value={dcVars.neuer_stundenlohn} onChange={e => setDcVars(v => ({...v, neuer_stundenlohn: e.target.value}))}
                          placeholder="z.B. 14.50" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                      <div className="space-y-1.5 col-span-2">
                        <Label className="text-xs text-muted-foreground">Neuer Monatslohn (€) <span className="text-muted-foreground/60 text-xs">(optional)</span></Label>
                        <input type="number" step="0.01" value={dcVars.neuer_monatslohn} onChange={e => setDcVars(v => ({...v, neuer_monatslohn: e.target.value}))}
                          placeholder="z.B. 2400" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                      </div>
                      <div className="space-y-1.5 col-span-2">
                        <Label className="text-xs text-muted-foreground">Zusatzinformationen <span className="text-muted-foreground/60 text-xs">(optional, für <code className="bg-muted px-1 rounded">&#123;&#123;zusatz_info&#125;&#125;</code>)</span></Label>
                        <textarea value={dcVars.zusatz_info} onChange={e => setDcVars(v => ({...v, zusatz_info: e.target.value}))}
                          rows={3} placeholder="Weitere Vereinbarungen..."
                          className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
                      </div>
                    </div>
                  </div>
                )}

              </div>
                )})()}

              {/* Mitarbeiter Adressdaten */}
              <div className="pt-1 border-t border-border/60">
                <p className="text-xs text-muted-foreground mb-2">Mitarbeiter-Adresse <span className="text-muted-foreground/60">(optional – für <code className="bg-muted px-1 rounded">&#123;&#123;an_strasse&#125;&#125;</code>, <code className="bg-muted px-1 rounded">&#123;&#123;an_plz_ort&#125;&#125;</code>)</span></p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Straße + Hausnummer</Label>
                    <input value={dcVars.an_strasse} onChange={e => setDcVars(v => ({...v, an_strasse: e.target.value}))}
                      placeholder="z.B. Musterstr. 5" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">PLZ / Ort</Label>
                    <input value={dcVars.an_plz_ort} onChange={e => setDcVars(v => ({...v, an_plz_ort: e.target.value}))}
                      placeholder="z.B. 70734 Fellbach" className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Geburtsdatum</Label>
                    <input type="date" value={dcVars.an_geburtsdatum} onChange={e => setDcVars(v => ({...v, an_geburtsdatum: e.target.value}))}
                      className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Unterschrift-Status */}
          {dcStep === 3 && (
            <div className="space-y-4 py-2">
              <div className="rounded-xl border p-4 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dcAlreadySigned}
                    onChange={e => setDcAlreadySigned(e.target.checked)}
                    className="mt-0.5 accent-primary h-4 w-4"
                    data-testid="dc-already-signed-check"
                  />
                  <div>
                    <p className="text-sm font-medium">Vertrag wurde bereits unterschrieben</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Der Vertrag ist bereits in Papierform abgeschlossen und wird hier nur digital archiviert.
                    </p>
                  </div>
                </label>
                {dcAlreadySigned && (
                  <div className="pl-7 space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Unterschriftsdatum (optional)</Label>
                    <input type="date" value={dcSignedDate} onChange={e => setDcSignedDate(e.target.value)}
                      className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="dc-signed-date-input" />
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="bg-muted/40 rounded-xl p-4 space-y-2 text-sm">
                <p className="font-medium text-foreground">Zusammenfassung</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <span className="text-muted-foreground">Mitarbeiter:</span>
                  <span>{employees.find(e => e.id === dcEmployeeId)?.name || '–'}</span>
                  <span className="text-muted-foreground">Vorlage:</span>
                  <span>{templates.find(t => t.id === dcTemplateId)?.name || '–'}</span>
                  <span className="text-muted-foreground">Eintrittsdatum:</span>
                  <span>{dcVars.eintrittsdatum ? formatDate(dcVars.eintrittsdatum) : '–'}</span>
                  <span className="text-muted-foreground">Status:</span>
                  <span className={dcAlreadySigned ? 'text-green-600 font-medium' : 'text-amber-600'}>
                    {dcAlreadySigned ? 'Bereits unterschrieben' : 'Offen (wartet auf Unterschrift)'}
                  </span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Der Vertrag wird DSGVO-konform verschlüsselt gespeichert und kann als PDF exportiert werden.
              </p>

              {/* ── Vertragsvorschau ─────────────────────────────────── */}
              <div className="border border-border rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b border-border">
                  <button
                    type="button"
                    onClick={() => setShowDcPreview(v => !v)}
                    className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
                    data-testid="dc-preview-toggle"
                  >
                    <FileText className="h-4 w-4 text-primary" />
                    Vertrag vorschauen
                    <span className="text-xs text-muted-foreground ml-1">
                      {showDcPreview ? '▲' : '▼'}
                    </span>
                  </button>
                  {showDcPreview && (
                    <div className="flex rounded-lg border border-border overflow-hidden text-xs">
                      <button
                        type="button"
                        onClick={() => setDcPreviewMode('rendered')}
                        className={`px-3 py-1.5 font-medium transition-colors ${dcPreviewMode === 'rendered' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                        data-testid="dc-preview-mode-rendered"
                      >
                        Vorschau
                      </button>
                      <button
                        type="button"
                        onClick={() => setDcPreviewMode('raw')}
                        className={`px-3 py-1.5 font-medium transition-colors ${dcPreviewMode === 'raw' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                        data-testid="dc-preview-mode-raw"
                      >
                        Vorlage
                      </button>
                    </div>
                  )}
                </div>
                {showDcPreview && (
                  dcPreviewMode === 'rendered' ? (
                    /* Editierbares Textarea für Vorschau-Modus */
                    <div className="border-t border-border">
                      <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800">
                        <span className="text-xs text-amber-700 dark:text-amber-300 font-medium">
                          Vertragstext direkt bearbeiten – Änderungen werden beim Speichern verwendet
                        </span>
                        <button
                          type="button"
                          onClick={() => setDcEditableText(renderDcPreview())}
                          className="ml-auto text-xs text-amber-600 underline hover:text-amber-800 shrink-0"
                        >
                          Zurücksetzen
                        </button>
                      </div>
                      <textarea
                        value={dcEditableText}
                        onChange={e => setDcEditableText(e.target.value)}
                        className="w-full p-4 max-h-[50vh] overflow-y-auto bg-background text-xs leading-relaxed font-mono border-0 focus:outline-none resize-none"
                        style={{ minHeight: '300px' }}
                        spellCheck={false}
                        data-testid="dc-preview-textarea"
                      />
                    </div>
                  ) : (
                    <div
                      className="p-4 max-h-[50vh] overflow-y-auto bg-background text-xs leading-relaxed whitespace-pre-wrap font-mono border-t border-border"
                      data-testid="dc-preview-text"
                    >
                      {templates.find(t => t.id === dcTemplateId)?.template_text || 'Keine Vorlage ausgewählt.'}
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            {dcStep > 1 && (
              <Button variant="outline" onClick={() => setDcStep(s => s - 1)}>Zurück</Button>
            )}
            <Button variant="ghost" onClick={() => setShowDirectContract(false)}>Abbrechen</Button>
            {dcStep < 3 ? (
              <Button
                onClick={() => setDcStep(s => s + 1)}
                disabled={dcStep === 1 && (!dcEmployeeId || !dcTemplateId)}
                data-testid="dc-next-btn"
              >
                Weiter
              </Button>
            ) : (
              <Button onClick={handleCreateDirectContract} disabled={dcSaving} data-testid="dc-save-btn">
                {dcSaving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Wird gespeichert…</> : <><FileText className="h-4 w-4 mr-1.5" /> Vertrag speichern</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hardware-Export Dialog */}
      <Dialog open={showHWDialog} onOpenChange={setShowHWDialog}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" /> Hardware-Liste exportieren
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <Label className="text-sm font-medium mb-3 block">Filter</Label>
              <div className="space-y-1">
                {[
                  { value: 'all',      label: 'Alle Mitarbeiter' },
                  { value: 'active',   label: 'Nur Aktive' },
                  { value: 'inactive', label: 'Nur Ausgeschiedene' },
                  { value: 'selection', label: 'Manuelle Auswahl' },
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2.5 cursor-pointer px-3 py-2.5 rounded-lg hover:bg-muted transition-colors">
                    <input
                      type="radio"
                      name="hwFilter"
                      value={opt.value}
                      checked={hwFilter === opt.value}
                      onChange={() => { setHwFilter(opt.value); if (opt.value !== 'selection') setHwSelection([]); }}
                      className="accent-primary"
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {hwFilter === 'selection' && (
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Mitarbeiter auswählen:</Label>
                <div className="border rounded-lg p-2 max-h-44 overflow-y-auto space-y-0.5">
                  {employees.map(emp => (
                    <label key={emp.id} className="flex items-center gap-2 px-2 py-2 cursor-pointer hover:bg-muted rounded-md">
                      <input
                        type="checkbox"
                        checked={hwSelection.includes(emp.id)}
                        onChange={e => {
                          if (e.target.checked) setHwSelection(p => [...p, emp.id]);
                          else setHwSelection(p => p.filter(id => id !== emp.id));
                        }}
                        className="accent-primary"
                      />
                      <span className="text-sm">{emp.name || emp.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHWDialog(false)}>Abbrechen</Button>
            <Button
              onClick={handleHardwareExport}
              disabled={exportingPdf || (hwFilter === 'selection' && hwSelection.length === 0)}
              data-testid="confirm-hardware-export-btn"
            >
              {exportingPdf
                ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Erstelle PDF…</>
                : <><FileDown className="h-4 w-4 mr-1.5" /> PDF exportieren</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ─── AG SIGNATUR DIALOG ──────────────────────────────────────────── */}
      <Dialog open={!!showSignAG} onOpenChange={open => !open && setShowSignAG(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Arbeitgeber digital unterschreiben</DialogTitle>
            <DialogDescription>
              {showSignAG && (
                <span>Vertrag mit <b>{showSignAG.employee_name || '–'}</b>
                  {showSignAG.signature_ag === 'already_signed' && (
                    <span className="ml-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                      Ersetzt die "bereits unterschrieben"-Markierung durch echte Signatur
                    </span>
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Datum</Label>
                <input type="text" value={agDatum} onChange={e => setAgDatum(e.target.value)}
                  placeholder="06.04.2026"
                  className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Ort</Label>
                <input type="text" value={agOrt} onChange={e => setAgOrt(e.target.value)}
                  placeholder="Fellbach"
                  className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Unterschrift Arbeitgeber</Label>
              <div className="border-2 border-dashed border-border rounded-xl overflow-hidden bg-white">
                <SignaturePad
                  ref={ref => setAgSigRef(ref)}
                  penColor="#1a1a2e"
                  height={180}
                />
              </div>
              <button onClick={() => agSigRef?.clear()}
                className="text-xs text-muted-foreground hover:text-foreground underline">
                Unterschrift löschen
              </button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignAG(null)}>Abbrechen</Button>
            <Button onClick={handleSignAG} disabled={agSigning} className="gap-2" data-testid="ag-sign-submit">
              {agSigning ? <><Loader2 className="h-4 w-4 animate-spin" /> Speichert…</> : 'Jetzt gegenzeichnen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── RV-BEFREIUNG: ARBEITGEBER-GEGENZEICHNUNG (Personalbogen-RV) ────── */}
      <Dialog open={!!showSignRvAG} onOpenChange={open => !open && setShowSignRvAG(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>RV-Befreiung – Arbeitgeber bestätigen</DialogTitle>
            <DialogDescription>
              {showSignRvAG && (
                <span>Bestätigung des Eingangs & Weiterleitung des Rentenversicherungs-Befreiungsantrags. Die Unterschrift des Mitarbeiters bleibt unverändert erhalten.</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Eingangsdatum des Antrags</Label>
                <input type="text" value={rvEingang} onChange={e => setRvEingang(e.target.value)}
                  placeholder="06.04.2026" data-testid="rv-eingang-input"
                  className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Ort</Label>
                <input type="text" value={rvOrt} onChange={e => setRvOrt(e.target.value)}
                  placeholder="Fellbach" data-testid="rv-ort-input"
                  className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Datum der Unterschrift</Label>
              <input type="text" value={rvDatum} onChange={e => setRvDatum(e.target.value)}
                placeholder="06.04.2026" data-testid="rv-datum-input"
                className="w-full h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Unterschrift Arbeitgeber</Label>
              <div className="border-2 border-dashed border-border rounded-xl overflow-hidden bg-white">
                <SignaturePad ref={ref => setRvSigRef(ref)} penColor="#1a1a2e" height={180} />
              </div>
              <button onClick={() => rvSigRef?.clear()}
                className="text-xs text-muted-foreground hover:text-foreground underline">
                Unterschrift löschen
              </button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignRvAG(null)}>Abbrechen</Button>
            <Button onClick={handleSignRvAG} disabled={rvSigning} className="gap-2" data-testid="rv-sign-ag-submit">
              {rvSigning ? <><Loader2 className="h-4 w-4 animate-spin" /> Speichert…</> : 'RV bestätigen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── 1-KLICK VERTRAGSGENERIERUNG ─────────────────────────────────── */}
      <ContractCreateModal
        open={!!contractCreateBogen}
        onClose={() => setContractCreateBogen(null)}
        bogen={contractCreateBogen}
        templates={templates}
        headers={headers}
        onSuccess={(signing, navigateToVertraege) => {
          fetchAll();
          if (navigateToVertraege) setTab('vertraege');
        }}
      />

      {/* ─── ACCOUNT ERSTELLT – PIN-ANZEIGE ─────────────────────────────── */}
      <Dialog open={!!newAccountInfo} onOpenChange={() => setNewAccountInfo(null)}>
        <DialogContent className="max-w-sm mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Account erstellt!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            {/* Erfolgs-Banner */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3 text-sm text-green-800 dark:text-green-300">
              <p className="font-semibold">{newAccountInfo?.name}</p>
              <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">{newAccountInfo?.email}</p>
            </div>

            {/* PIN – groß und auffällig */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-xl p-5 text-center">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-2">
                Einmal-Passwort (4-stellig)
              </p>
              <p className="text-5xl font-bold font-mono tracking-[0.4em] text-amber-800 dark:text-amber-300 select-all">
                {newAccountInfo?.pin}
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-3 leading-relaxed">
                Dieses Passwort <strong>jetzt mündlich</strong> an den Mitarbeiter weitergeben.<br />
                Er kann es nach dem ersten Login selbst ändern.
              </p>
            </div>

            {/* Login-Info Box */}
            <div className="bg-muted/50 rounded-lg p-3 text-xs space-y-1 text-muted-foreground">
              <p className="font-medium text-foreground">Login-Daten für den Mitarbeiter:</p>
              <p>App: <span className="font-mono text-foreground">https://app.allflowx.de</span></p>
              <p>E-Mail: <span className="font-mono text-foreground">{newAccountInfo?.email}</span></p>
              <p>Passwort: <span className="font-mono font-bold text-amber-700 dark:text-amber-400">{newAccountInfo?.pin}</span></p>
            </div>
          </div>
          <DialogFooter>
            <Button className="w-full" onClick={() => setNewAccountInfo(null)} data-testid="close-account-pin-btn">
              Verstanden – PIN wurde mitgeteilt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── SIGNIERLINK + PIN DIALOG ─────────────────────────────────────── */}
      <Dialog open={!!signingLinkInfo} onOpenChange={() => setSigningLinkInfo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-primary" />
              Signierlink für {signingLinkInfo?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* PIN – prominent anzeigen */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1.5">
                PIN-Code für den Mitarbeiter
              </p>
              <p className="text-3xl font-bold font-mono tracking-[0.4em] text-amber-800 dark:text-amber-300">
                {signingLinkInfo?.pin_code || '–'}
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-2">
                Den PIN-Code dem Mitarbeiter <strong>persönlich</strong> mitteilen (nicht per E-Mail). 
                Er braucht ihn zum Öffnen des Vertrags.
              </p>
            </div>

            {/* Link */}
            <div className="space-y-1.5">
              <Label className="text-sm">Signierlink</Label>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={signingLinkInfo?.url || ''}
                  className="flex-1 h-9 rounded-md border bg-muted px-3 text-xs font-mono text-muted-foreground"
                  data-testid="signing-link-display"
                />
                <Button variant="outline" size="sm" onClick={() => {
                  navigator.clipboard.writeText(signingLinkInfo?.url || '');
                  toast.success('Link kopiert!');
                }}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* E-Mail senden via Brevo (Link, kein PIN) */}
            <Button
              className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={async () => {
                try {
                  const s = signingLinkInfo?.signing;
                  if (!s?.id) { toast.error('Signing-ID fehlt'); return; }
                  const res = await axios.post(`${API}/contracts/signing/${s.id}/send-signing-link-email`, {}, { headers });
                  toast.success(`Link per E-Mail an ${res.data.sent_to} gesendet! PIN mündlich mitteilen.`);
                } catch (e) { toast.error(e.response?.data?.detail || 'E-Mail-Versand fehlgeschlagen'); }
              }}
            >
              <Mail className="h-4 w-4" /> Per E-Mail senden (Link ohne PIN)
            </Button>

            {/* WhatsApp */}
            <Button
              variant="outline"
              className="w-full gap-2 border-green-300 text-green-700 hover:bg-green-50"
              onClick={() => {
                const msg = `Hallo ${signingLinkInfo?.name}! 👋\n\nBitte unterschreibe deinen Arbeitsvertrag:\n\n🔗 ${signingLinkInfo?.url}\n\nDen PIN-Code erhältst du von mir persönlich.`;
                window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
              }}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Per WhatsApp teilen (Link, ohne PIN)
            </Button>

            <p className="text-xs text-muted-foreground text-center bg-muted/40 rounded-lg p-2">
              Link per WhatsApp/E-Mail senden – PIN <strong>mündlich</strong> mitteilen (DSGVO).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigningLinkInfo(null)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── ARCHIVIERUNGS-DIALOG ────────────────────────────────────────── */}
      <Dialog open={!!archiveDialog} onOpenChange={() => setArchiveDialog(null)}>
        <DialogContent className="max-w-sm mx-4 sm:mx-auto">
          {archiveDialog?.step === 'choose' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-muted-foreground" />
                  Mitarbeiter entfernen
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <p className="text-sm text-muted-foreground">
                  Was soll mit <strong>{archiveDialog.emp?.name || archiveDialog.emp?.email}</strong> passieren?
                </p>

                {/* Option: Archivieren */}
                <button
                  onClick={() => {
                    if (archiveDialog?.hasZeugnis) {
                      // Zeugnis bereits vorhanden → direkt archivieren ohne Frage
                      handleArchiveEmployee(archiveDialog.emp);
                    } else {
                      setArchiveDialog(d => ({...d, step: 'archive_zeugnis'}));
                    }
                  }}
                  className="w-full text-left p-4 rounded-xl border-2 border-amber-200 bg-amber-50 dark:bg-amber-900/10 hover:border-amber-400 transition-all"
                  data-testid="archive-option-btn"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                      <Archive className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-amber-800 dark:text-amber-300 text-sm">Archivieren</p>
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5 leading-relaxed">
                        Daten bleiben erhalten. Kein Zugang, keine Abrechnung.
                        Jederzeit wiederherstellbar.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Option: Endgültig löschen */}
                <button
                  onClick={() => setArchiveDialog(d => ({...d, step: 'delete_confirm'}))}
                  className="w-full text-left p-4 rounded-xl border-2 border-red-200 bg-red-50 dark:bg-red-900/10 hover:border-red-400 transition-all"
                  data-testid="delete-option-btn"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
                      <Trash2 className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-red-800 dark:text-red-300 text-sm">Endgültig löschen</p>
                      <p className="text-xs text-red-700 dark:text-red-400 mt-0.5 leading-relaxed">
                        Alle Daten werden permanent gelöscht. Nicht rückgängig machbar.
                      </p>
                    </div>
                  </div>
                </button>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setArchiveDialog(null)}>Abbrechen</Button>
              </DialogFooter>
            </>
          )}

          {/* Zeugnis-Frage vor Archivierung */}
          {archiveDialog?.step === 'archive_zeugnis' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Archive className="h-5 w-5 text-amber-600" />
                  Vor dem Archivieren
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <p className="text-sm text-muted-foreground">
                  Möchten Sie vorher noch ein <strong>Arbeitszeugnis</strong> für{' '}
                  <strong>{archiveDialog.emp?.name}</strong> erstellen?
                </p>
                <div className="flex flex-col gap-2">
                  <Button variant="outline" className="gap-2 border-violet-300 text-violet-700 hover:bg-violet-50"
                    onClick={() => {
                      setShowZeugnisForArchive(archiveDialog.emp);
                      setArchiveDialog(null);
                    }}>
                    <FileText className="h-4 w-4" /> Ja, Zeugnis erstellen
                  </Button>
                  <Button className="gap-2 bg-amber-600 hover:bg-amber-700"
                    onClick={() => handleArchiveEmployee(archiveDialog.emp)}
                    data-testid="confirm-archive-btn">
                    <Archive className="h-4 w-4" /> Ohne Zeugnis archivieren
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" size="sm" onClick={() => setArchiveDialog(d => ({...d, step: 'choose'}))}>
                  ← Zurück
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Endgültig löschen Bestätigung */}
          {(archiveDialog?.step === 'delete_confirm' || archiveDialog?.step === 'delete_confirm_archived') && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-destructive">
                  <Trash2 className="h-5 w-5" />
                  Endgültig löschen
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                  <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                    Sicher? Diese Aktion kann nicht rückgängig gemacht werden!
                  </p>
                  <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                    Alle Daten von <strong>{archiveDialog.emp?.name || archiveDialog.emp?.email}</strong>{' '}
                    werden dauerhaft gelöscht.
                  </p>
                </div>
              </div>
              <DialogFooter className="flex gap-2">
                <Button variant="ghost"
                  onClick={() => setArchiveDialog(d => ({...d, step: d.step === 'delete_confirm_archived' ? null : 'choose'}))}>
                  Abbrechen
                </Button>
                <Button variant="destructive"
                  onClick={() => handlePermanentDelete(archiveDialog.emp)}
                  data-testid="confirm-delete-btn">
                  Endgültig löschen
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Zeugnis nach Archivierung (zeigt das Zeugnis-Modal für einen Mitarbeiter im Archiv-Context) */}
      {showZeugnisForArchive && (
        <Dialog open={true} onOpenChange={() => setShowZeugnisForArchive(null)}>
          <DialogContent className="p-0 max-w-2xl max-h-[92vh] flex flex-col gap-0">
            <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-violet-600" />
                Zeugnis für {showZeugnisForArchive?.name} – dann archivieren
              </DialogTitle>
            </DialogHeader>
            <div className="p-6 text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Öffnen Sie das Mitarbeiter-Profil, um dort das KI-Zeugnis zu erstellen.
                Danach kehren Sie hierher zurück und archivieren den Mitarbeiter.
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" className="gap-2"
                  onClick={() => { navigate(`/admin/personal/mitarbeiter/${showZeugnisForArchive.id}`); setShowZeugnisForArchive(null); }}>
                  Zum Profil & Zeugnis erstellen →
                </Button>
                <Button className="gap-2 bg-amber-600 hover:bg-amber-700"
                  onClick={() => { handleArchiveEmployee(showZeugnisForArchive); setShowZeugnisForArchive(null); }}>
                  <Archive className="h-4 w-4" /> Direkt archivieren
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

    </DashboardLayout>
  );
}
